// pages/Login.js - Login Page
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, LayoutDashboard, ShieldCheck, Loader2, ArrowBigUp } from 'lucide-react';

// Latar panel kiri: beberapa radial-gradient sangat pucat ditumpuk di atas dasar
// putih kebiruan. Dua lapis teratas memakai CSS variable brand-* dari index.css,
// jadi kalau palet brand diganti, gradasinya ikut menyesuaikan.
// Noise/grain sangat halus. Dibuat dari feTurbulence sebagai data-URI supaya
// tidak ada file aset tambahan dan tetap tajam di layar HiDPI. Opacity-nya
// sengaja di bawah 2% — tujuannya menghilangkan kesan gradien CSS yang terlalu
// "bersih", bukan untuk terlihat sebagai tekstur.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.42'/%3E%3C/svg%3E\")";

const PANEL_BG = {
  backgroundColor: '#FBFCFE',
  backgroundImage: [
    'radial-gradient(120% 95% at 0% 0%, rgb(var(--brand-100) / 0.85) 0%, rgb(var(--brand-50) / 0.35) 42%, rgb(255 255 255 / 0) 70%)',
    'radial-gradient(85% 75% at 92% 8%, rgb(var(--brand-50) / 0.9) 0%, rgb(255 255 255 / 0) 62%)',
    'radial-gradient(95% 80% at 12% 102%, rgb(224 231 255 / 0.7) 0%, rgb(255 255 255 / 0) 66%)',
    'radial-gradient(70% 60% at 100% 88%, rgb(254 243 232 / 0.75) 0%, rgb(255 255 255 / 0) 60%)',
    'linear-gradient(165deg, #FFFFFF 0%, #F7F9FD 100%)',
  ].join(','),
};

// Rute transport: satu-satunya elemen dekoratif di panel. Titik kuning bergerak
// dari kiri ke kanan mengikuti arah promote request.
const ROUTE = [
  // tone: menentukan warna node + garis SESUDAHNYA. Ketiga tahap ditandai 'ok'
  // supaya rute terbaca mengalir penuh D01 -> Q01 -> P01 tanpa status tertahan.
  { id: 'D01', role: 'Development', note: 'client 130', tone: 'ok', badge: null },
  { id: 'Q01', role: 'Quality',     note: 'client 230', tone: 'ok', badge: null },
  { id: 'P01', role: 'Production',  note: 'client 310', tone: 'ok', badge: null },
];

// Satu tempat untuk semua warna status, supaya tidak tersebar di JSX.
const TONE = {
  ok:     { node: 'bg-emerald-500', ring: 'ring-emerald-100', label: 'text-slate-700' },
  queued: { node: 'bg-brand-500',   ring: 'ring-brand-100',   label: 'text-slate-700' },
  locked: { node: 'bg-slate-400',   ring: 'ring-slate-100',   label: 'text-slate-500' },
};

function TransportRoute() {
  return (
    <div className="relative pt-1" aria-hidden="true">
      <style>{`
        @keyframes pdTravel {
          /* Mengalir penuh D01 -> Q01 -> P01. Kecepatan dibuat rata (linear)
             supaya tidak ada kesan melambat/tertahan di tengah rute. */
          0%   { left: 16.666%; opacity: 0; }
          8%   { opacity: 1; }
          92%  { opacity: 1; }
          100% { left: 83.333%; opacity: 0; }
        }
        .pd-pack {
          animation: pdTravel 7s linear infinite;
          box-shadow: 0 0 12px 2px rgb(var(--brand-500) / 0.45);
        }
        @media (prefers-reduced-motion: reduce) {
          /* Fallback statis: packet diparkir di tengah rute sehingga jalur
             tetap terbaca menyambung tanpa gerakan. */
          .pd-pack { animation: none; left: 50%; opacity: 1; }
        }
      `}</style>

      {/* Rute D01 -> Q01 -> P01: satu garis solid, seluruh jalur mengalir. */}
      <div className="absolute left-[16.666%] right-[16.666%] top-[8px] h-px bg-slate-300/80" />

      <div className="pd-pack absolute top-[6px] h-[3px] w-6 -translate-x-1/2 rounded-full bg-brand-400" />
      <div className="pd-pack absolute top-[6px] h-[3px] w-6 -translate-x-1/2 rounded-full bg-brand-400" style={{ animationDelay: '2.4s' }} />
      <div className="pd-pack absolute top-[6px] h-[3px] w-6 -translate-x-1/2 rounded-full bg-brand-400" style={{ animationDelay: '4.8s' }} />

      <div className="relative grid grid-cols-3">
        {ROUTE.map(s => {
          const tone = TONE[s.tone];
          return (
            <div key={s.id} className="relative flex flex-col items-center text-center">
              {/* Badge jumlah antrean; menempel di atas-kanan node. */}
              {s.badge && (
                <span className="absolute -top-[18px] left-1/2 ml-1.5 rounded-full bg-brand-500 px-1.5 py-px font-mono text-[10px] font-semibold leading-[1.4] tabular-nums text-white">
                  {s.badge}
                </span>
              )}
              <span className={`h-[9px] w-[9px] rotate-45 ring-4 ring-white ${tone.node}`} />
              <p className={`mt-3.5 font-mono text-sm font-semibold tabular-nums ${tone.label}`}>{s.id}</p>
              <p className="mt-1 font-sans text-[11px] font-medium text-slate-500">{s.role}</p>
              <p className="mt-0.5 font-mono text-[11px] tabular-nums text-slate-400">{s.note}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Login() {
  const { login, user, error, setError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // URL tujuan asli. Prioritas: sessionStorage (paling tahan banting) → location.state
  // → default /dashboard. Dibaca sekali saat mount agar tidak berubah-ubah.
  const fromRef = useRef(null);
  if (fromRef.current === null) {
    let target = location.state?.from;
    try {
      const stored = sessionStorage.getItem('postLoginRedirect');
      if (stored) target = stored;
    } catch { /* abaikan */ }
    fromRef.current = target || '/dashboard';
  }
  const from = fromRef.current;

  const [form, setForm]         = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  // Caps Lock aktif adalah penyebab gagal login yang paling sering dan paling
  // tidak terlihat — password tertutup titik, jadi user tidak punya petunjuk.
  const [capsOn, setCapsOn] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [fieldErr, setFieldErr] = useState({});
  const [logoutNotice, setLogoutNotice] = useState(null); // null | 'idle' | 'revoked' | 'expired'

  // Tampilkan notif bila sesi sebelumnya berakhir (idle / force-logout admin /
  // token kedaluwarsa). 'expired' diberi kalimatnya sendiri: menyebutnya
  // "inactivity" seperti cabang idle akan menyesatkan justru pada kasus yang
  // paling sering terjadi saat user sedang AKTIF menunggu jawaban panjang.
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('logout_reason');
      if (reason === 'idle' || reason === 'revoked' || reason === 'expired') {
        setLogoutNotice(reason);
        sessionStorage.removeItem('logout_reason');
      }
    } catch (_) { /* abaikan */ }
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, navigate, from]);

  // Reset error when form changes
  useEffect(() => {
    setError(null);
    setFieldErr({});
  }, [form, setError]);

  const validate = () => {
    const err = {};
    if (!form.username.trim()) err.username = 'Username is required';
    if (!form.password.trim()) err.password = 'Password is required';
    return err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setFieldErr(errs); return; }

    setLoading(true);
    const result = await login(form.username.trim(), form.password);
    setLoading(false);

    if (result.success) {
      // Tandai sesi sebagai "lewat login" (dipakai Sidebar untuk auto-hide device-based)
      // dan bersihkan tujuan tersimpan agar tidak terpakai ulang.
      try {
        sessionStorage.setItem('sb_login_session', '1');
        sessionStorage.removeItem('postLoginRedirect');
      } catch { /* abaikan */ }
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white lg:flex">

      {/* ── Left panel: identity ────────────────────────────────────────── */}
      <aside
        className="relative hidden overflow-hidden border-slate-200/70 lg:flex lg:w-1/2 lg:flex-col lg:justify-between lg:border-r"
        style={PANEL_BG}
      >
        {/* Grain overlay — pointer-events-none supaya tidak mengganggu klik. */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.018] mix-blend-multiply"
          style={{ backgroundImage: GRAIN, backgroundSize: '140px 140px' }}
          aria-hidden="true"
        />

        <div className="relative flex items-center gap-3 p-10 xl:p-12">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-600 shadow-sm">
            <LayoutDashboard className="h-5 w-5 text-white" />
          </div>
          <span className="font-display text-sm font-semibold tracking-[-0.011em] text-slate-800">
            Primary Dashboard
          </span>
        </div>

        <div className="relative px-10 xl:px-12">
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-brand-800">
            Pacific Indomas · IT operations
          </p>
          {/* Satu frasa diberi warna brand sebagai titik fokus — ini juga yang
              mengikat headline ke warna aksen tombol & timeline. */}
          <h2 className="font-display mt-6 max-w-[20ch] text-display-md font-semibold text-slate-800 xl:text-display-lg">
            Your entire landscape in a{' '}
            <span className="text-brand-700">single view</span>.
          </h2>
          <p className="mt-6 max-w-[48ch] text-[15px] leading-[1.65] text-slate-500/95">
            Transport queues, ABAP dumps, AWS spend, Microsoft 365 licenses, and
            service desk tickets — read from each source system and brought
            together in one place.
          </p>
        </div>

        <div className="relative px-10 pb-11 pt-14 xl:px-12">
          <TransportRoute />
        </div>
      </aside>

      {/* ── Right panel: form ───────────────────────────────────────────── */}
      {/* Panel kanan diberi dasar abu sangat tipis. Sebelumnya kartu putih
          berdiri di atas kanvas putih, jadi terlihat "mengambang" tanpa alas. */}
      <main className="flex flex-1 flex-col justify-center bg-slate-50/70 px-4 py-10 sm:px-8">

        {/* Compact header for small screens, where the left panel is hidden */}
        <div className="mx-auto mb-8 w-full max-w-md text-center lg:hidden">
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 shadow-md">
            <LayoutDashboard className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-display text-display-sm font-semibold text-slate-800">
            Primary Dashboard
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            Integrated Monitoring System — SAP &amp; AWS
          </p>
        </div>

        <div className="mx-auto w-full max-w-md">
          {/* Bayangan diganti: lebih rendah tapi lebih lebar, plus ring 1px.
              Efeknya kartu terbaca "menempel" pada permukaan, bukan melayang. */}
          <div className="rounded-3xl bg-white p-8 ring-1 ring-slate-900/[0.06] shadow-[0_1px_2px_rgb(15_23_42/0.04),0_12px_32px_-12px_rgb(15_23_42/0.12)]">
            <div className="mb-6 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-brand-600" />
              <h2 className="font-display text-[17px] font-semibold tracking-[-0.012em] text-slate-700">
                Sign in to your account
              </h2>
            </div>

            {logoutNotice && (
              <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-amber-500" />
                <p className="text-sm font-medium text-amber-700">
                  {logoutNotice === 'revoked'
                    ? 'Your session was ended by an administrator. Please sign in again.'
                    : logoutNotice === 'expired'
                      ? 'Your session expired. Please sign in again — any work still running will be waiting for you.'
                      : 'Your session ended due to inactivity. Please sign in again.'}
                </p>
              </div>
            )}

            {error && (
              <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                <div className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full bg-red-500" />
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Username</label>
                <input
                  type="text"
                  autoComplete="username"
                  value={form.username}
                  onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                  placeholder="Enter username"
                  className={`w-full rounded-xl border px-4 py-3 text-sm font-medium outline-none transition-all duration-200
                    ${fieldErr.username ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-[3px] focus:ring-red-500/20' : 'border-slate-200 bg-slate-50/80 hover:border-slate-300 focus:border-brand-400 focus:bg-white focus:ring-[3px] focus:ring-brand-500/20'}`}
                />
                {fieldErr.username && <p className="mt-1.5 text-xs text-red-600">{fieldErr.username}</p>}
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    onKeyUp={e => setCapsOn(e.getModifierState && e.getModifierState('CapsLock'))}
                    onKeyDown={e => setCapsOn(e.getModifierState && e.getModifierState('CapsLock'))}
                    onBlur={() => setCapsOn(false)}
                    placeholder="Enter password"
                    className={`w-full rounded-xl border px-4 py-3 pr-12 text-sm font-medium outline-none transition-all duration-200
                      ${fieldErr.password ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-[3px] focus:ring-red-500/20' : 'border-slate-200 bg-slate-50/80 hover:border-slate-300 focus:border-brand-400 focus:bg-white focus:ring-[3px] focus:ring-brand-500/20'}`}
                  />
                  <button
                    type="button"
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPass(p => !p)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
                  >
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {fieldErr.password && <p className="mt-1.5 text-xs text-red-600">{fieldErr.password}</p>}
                {capsOn && !fieldErr.password && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-700" role="status">
                    <ArrowBigUp className="h-3.5 w-3.5" aria-hidden="true" />
                    Caps Lock is on
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl
                  bg-gradient-to-b from-brand-600 to-brand-700 px-4 py-3
                  text-sm font-semibold text-white
                  shadow-[inset_0_1px_0_rgb(255_255_255/0.25),0_6px_16px_-6px_rgb(var(--brand-600)/0.65)]
                  transition-[filter,transform,box-shadow] duration-150
                  hover:brightness-[1.04]
                  active:translate-y-px active:brightness-[0.97]
                  focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-500/35
                  disabled:cursor-not-allowed disabled:from-brand-400 disabled:to-brand-400
                  disabled:shadow-none disabled:brightness-100 motion-reduce:active:translate-y-0"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : 'Sign In'}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center font-mono text-[11px] text-slate-400">
            Internal application · corporate network only
          </p>
          <p className="mt-2 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} Primary Dashboard · Integrated Monitoring System
          </p>
        </div>
      </main>
    </div>
  );
}
